import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Button, TextField, Select, MenuItem, FormControl, InputLabel, InputAdornment, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SettingsExclusionView from "./SettingsExclusionView";
import { useSettingsExclusion } from "../../../hooks/useSettingsExclusion";
import useShowMessages from "../../../hooks/useShowMessages";

const parseLicensePlateExclusions = (value) => {
    const plates = Array.isArray(value) ? value : [];
    return plates.map((item, index) => ({
        id: index + 1,
        license_plate: item.license_plate,
        state: item.state,
    }));
};

const calculateLicensePlateDiff = (initialItems, currentItems) => {
    const makeKey = (item) => `${item.license_plate}|${item.state}`;
    const initialSet = new Set(initialItems.map(makeKey));
    const currentSet = new Set(currentItems.map(makeKey));
    const diff = [];

    initialItems.forEach((item) => {
        if (!currentSet.has(makeKey(item))) {
            diff.push({ ...item, isDeleted: true });
        }
    });
    currentItems.forEach((item) => {
        if (!initialSet.has(makeKey(item))) {
            diff.push({ ...item, isDeleted: false });
        }
    });

    return diff;
};

const SettingsExclusionLicensePlate = ({ onClose, openSuccessModal, updateSetting, settingSelected, storeStateCodes }) => {
    const { showMessage } = useShowMessages();
    const [exclusionValue, setExclusionValue] = useState("");
    const [stateValue, setStateValue] = useState(storeStateCodes?.[0] || "");

    const settingsExclusion = useSettingsExclusion({
        settingSelected,
        parseInitialData: parseLicensePlateExclusions,
        calculateDiff: calculateLicensePlateDiff,
        updateSetting,
        onClose,
        openSuccessModal,
    });

    const handleAddExclusion = useCallback(() => {
        const trimmedValue = exclusionValue.trim().toUpperCase();
        if (!trimmedValue) {
            showMessage("Please enter a license plate.", "warning");
            return;
        }
        if (settingsExclusion.items.some(ex => ex.license_plate === trimmedValue && ex.state === stateValue)) {
            showMessage("This exclusion already exists.", "warning");
            return;
        }

        settingsExclusion.setItems((prev) => [
            { id: Date.now(), license_plate: trimmedValue, state: stateValue },
            ...prev,
        ]);
        setExclusionValue("");
        showMessage("Exclusion added to the list.", "success");
    }, [exclusionValue, stateValue, settingsExclusion.setItems, settingsExclusion.items, showMessage]);

    const columns = useMemo(() => [
        { field: "license_plate", headerName: "LICENSE PLATE", flex: 1 },
        { field: "state", headerName: "STATE", flex: 1 },
    ], []);
    
    const renderInputForm = () => (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="state-label">Select State</InputLabel>
                <Select labelId="state-label" value={stateValue} label="Select State" onChange={(e) => setStateValue(e.target.value)}>
                    {storeStateCodes?.map((state) => 
                        (<MenuItem key={state} value={state}>{state}</MenuItem>)
                    )}
                </Select>
            </FormControl>
            <TextField
                label="License Plate"
                value={exclusionValue}
                onChange={(e) => setExclusionValue(e.target.value.toUpperCase())}
                sx={{ flex: 1 }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => setExclusionValue("")} edge="end"><ClearIcon /></IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <Button variant="contained" onClick={handleAddExclusion} className="button red small">Add</Button>
        </Box>
    );

    return (
        <SettingsExclusionView
            title={settingSelected?.label || "License Plate Exclusion"}
            columns={columns}
            renderInputForm={renderInputForm}
            onClose={onClose}
            datagridProps={{
                disableColumnFilter: false,
            }}
            {...settingsExclusion}
        />
    );
};

SettingsExclusionLicensePlate.propTypes = {
    onClose: PropTypes.func.isRequired,
    openSuccessModal: PropTypes.func.isRequired,
    updateSetting: PropTypes.func.isRequired,
    settingSelected: PropTypes.object,
    storeStateCodes: PropTypes.arrayOf(PropTypes.string),
};

export default SettingsExclusionLicensePlate;